import { useState } from 'react';
import type { FormEvent } from 'react';

import { CLICK_IDS } from '@sunflower-games/analytics-events';
import type { TFunction } from 'i18next';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Button from '../../../components/buttons/Button';
import { DialogContent } from '../../../components/Dialog/DialogContent';
import { DialogHeader } from '../../../components/Dialog/DialogHeader';
import { DialogLoader } from '../../../components/Dialog/DialogLoader';
import { ErrorMessage } from '../../../components/ErrorMessage/ErrorMessage';
import { FormInput } from '../../../components/FormInput/FormInput';
import { PasswordInput } from '../../../components/FormInput/PasswordInput';
import type { ErrorMessageMap } from '../../../components/FormInput/types';
import type { WizardChildProps } from '../../../components/Wizard/types';
import { analytics } from '../../../services/analytics';
import { useStores } from '../../../stores/useStores';
import { ThirdPartyAuth } from '../ThirdPartyAuth/ThirdPartyAuth';
import { useHandleAuth } from '../useHandleAuth';
import { useOpenAuthWizard } from '../useOpenAuthWizard';

import styles from './LogIn.module.scss';

interface LoginForm extends HTMLFormElement {
  elements: HTMLFormControlsCollection & {
    email: HTMLInputElement;
    password: HTMLInputElement;
  };
}
const emailErrorMessageMap = (t: TFunction): ErrorMessageMap => ({
  typeMismatch: t('validation.emailInvalid'),
  valueMissing: t('validation.emailRequired'),
});

const passwordErrorMessageMap = (t: TFunction): ErrorMessageMap => ({
  valueMissing: t('validation.passwordRequired'),
});

export const LogIn = observer((props: WizardChildProps) => {
  const { t } = useTranslation();
  const { authStore, portalStore } = useStores();

  const { openAuthWizard } = useOpenAuthWizard();

  const landingKey = authStore.getLandingKey();
  const isDirectLanding = authStore.isDirectLanding();

  const [error, setError] = useState<string>();

  const { handleAuth } = useHandleAuth(setError, props.nextStep);

  const clearError = () => setError(undefined);

  const isProcessing = computed(() => authStore.pending('signIn')).get();

  const openResetPassword = () => {
    void analytics.sendEvent('log_in', {
      step_name: 'forgot_password',
      event_action: 'click',
      click_id: CLICK_IDS.AUTH_FLOW.START_RESET_PASSWORD,
      landingKey,
    });

    openAuthWizard('resetPasswordWizard', {
      force: true,
      persistent: isDirectLanding,
      landingKey: landingKey ?? undefined,
    });
  };

  const handleSubmit = (event: FormEvent<LoginForm>) => {
    event.preventDefault();
    const { email, password } = event.currentTarget.elements;
    void analytics.sendEvent('log_in', {
      step_name: 'log_in_platform',
      event_action: 'click',
      click_id: CLICK_IDS.AUTH_BUTTONS.LOG_IN.email,
      log_in_platform: 'email',
      triggered_by: portalStore.options.triggeredBy as never,
      landingKey,
    });

    void handleAuth({
      provider: 'email',
      action: 'log_in',
      credentials: {
        email: email.value,
        password: password.value,
      },
    });
  };

  const onGoBack = () => {
    void analytics.sendEvent('sign_up', {
      step_name: 'sign_up_start',
      button_location: 'log_in',
      event_action: 'click',
      click_id: CLICK_IDS.AUTH_FLOW.OPEN_SIGN_UP_FROM_LOGIN,
      triggered_by: 'click',
      button_name: 'sign_up',
      landingKey,
    });

    openAuthWizard('signUpWizard', {
      triggeredBy: 'log_in',
      force: true,
      persistent: isDirectLanding,
      landingKey: landingKey ?? undefined,
    });
  };

  return (
    <>
      <DialogHeader
        closeBtnDataTestId="close-login"
        title={t('auth.logIn')}
        onClose={portalStore.closePortal}
        onGoBack={isDirectLanding ? onGoBack : undefined}
      />

      <DialogLoader isProcessing={isProcessing} />

      <DialogContent className={styles.loginDialogContent}>
        <ThirdPartyAuth action="Log in" />

        <div className={styles.delimiter}>
          <hr />
          <span>{t('common.orLowerCase')}</span>
          <hr />
        </div>

        <form onChange={clearError} onSubmit={handleSubmit}>
          <div className={styles.formRow}>
            <label className="form-field__label" htmlFor="email">
              {t('common.email')}
            </label>
            <FormInput
              required
              autoComplete="email"
              data-testid="email-input"
              disabled={isProcessing}
              errorMessageMap={emailErrorMessageMap(t)}
              id="email"
              name="email"
              placeholder={t('forms.emailEmail')}
              type="email"
            />
          </div>

          <div className={styles.formRow}>
            <label className="form-field__label" htmlFor="password">
              {t('forms.password')}
            </label>
            <PasswordInput
              required
              disabled={isProcessing}
              errorMessageMap={passwordErrorMessageMap(t)}
              id="password"
              name="password"
              placeholder={t('auth.enterPassword')}
            />
          </div>

          <Button
            btnType="main"
            className={styles.logInButton}
            dataTestId="login-submit-btn"
            disabled={isProcessing}
            type="submit"
          >
            {t('auth.logIn')}
          </Button>

          <ErrorMessage>{error}</ErrorMessage>

          <div className="animation-slide-down form-links">
            <span>{t('auth.forgotPasswordQuestion')}</span>
            <span className="text-link" data-testid="login-forgot-password-link" onClick={openResetPassword}>
              {t('auth.clickHere')}
            </span>
          </div>
        </form>
      </DialogContent>
    </>
  );
});
